import { useMemo, useState } from 'react'
import type { Nutrient, UserData } from '../../types'
import { buildNutrition, periodWindowLabel, type Period } from '../../utils/analytics'
import {
  computeTargets,
  ACTIVITY_LABELS,
  ACTIVITY_MULTIPLIERS,
  GOAL_LABELS,
  type TargetKind,
  type Targets,
} from '../../utils/targets'
import { NUTRIENT_LABELS } from '../../utils/intake'
import { BarChart } from './ChartCanvas'
import Modal from '../layout/Modal'

type StatKey = 'bmi' | 'bmr' | 'tdee' | 'target'

const bmiBand = (b: number) => (b < 18.5 ? 'Underweight' : b < 25 ? 'Healthy' : b < 30 ? 'Overweight' : 'Obese')

/** Personalised explainer content for each "Your numbers" stat. */
function statInfo(key: StatKey, t: Targets, data: UserData): { title: string; body: React.ReactNode } {
  const activity = data.activityLevel
  const goal = data.goal
  const heightM = (data.heightCm || 0) / 100

  if (key === 'bmi') {
    const lo = heightM > 0 ? Math.round(18.5 * heightM * heightM) : null
    const hi = heightM > 0 ? Math.round(24.9 * heightM * heightM) : null
    return {
      title: 'BMI — Body Mass Index',
      body: (
        <>
          <p>Your weight relative to your height (kg ÷ m²). It's a quick population-level screen, not a body-fat measure — very muscular people can read “high” without excess fat.</p>
          <p className="mt-2">
            <strong>Yours:</strong> {t.bmi ? t.bmi.toFixed(1) : '—'} — {t.bmi ? bmiBand(t.bmi) : '—'}.
          </p>
          <p className="mt-2">
            <strong>Ideal:</strong> a healthy BMI is 18.5–24.9
            {lo && hi ? `, about ${lo}–${hi} kg for your height` : ''}.
          </p>
          <p className="mt-2 text-[var(--text-secondary)]">
            Bands: &lt;18.5 underweight · 18.5–24.9 healthy · 25–29.9 overweight · ≥30 obese.
          </p>
        </>
      ),
    }
  }
  if (key === 'bmr') {
    return {
      title: 'BMR — Basal Metabolic Rate',
      body: (
        <>
          <p>The energy your body uses at complete rest just to keep you alive — breathing, circulation, organs, body temperature. Estimated with the Mifflin–St Jeor equation from your weight, height, age and sex.</p>
          <p className="mt-2"><strong>Yours:</strong> {t.bmr} kcal/day.</p>
          <p className="mt-2">
            <strong>Ideal:</strong> there's no “good” or “bad” BMR — it's just your baseline and naturally varies with size, age, sex and muscle mass. Treat it as a floor: regularly eating below your BMR isn't advisable without professional guidance.
          </p>
        </>
      ),
    }
  }
  if (key === 'tdee') {
    const mult = activity ? ACTIVITY_MULTIPLIERS[activity] : null
    return {
      title: 'TDEE — Total Daily Energy Expenditure',
      body: (
        <>
          <p>Roughly all the calories you burn in a day: your BMR multiplied by an activity factor for how active you are.</p>
          <p className="mt-2">
            <strong>Yours:</strong> {t.tdee} kcal/day{mult && activity ? ` (BMR ${t.bmr} × ${mult} for “${ACTIVITY_LABELS[activity]}”)` : ''}.
          </p>
          <p className="mt-2">
            <strong>Ideal:</strong> this is your <em>maintenance</em> level — eat around it to hold weight, below it to lose, above it to gain.
          </p>
        </>
      ),
    }
  }
  // target
  const custom = data.customCalories && data.customCalories > 0
  const goalText =
    goal === 'lose'
      ? 'lose weight (−500 kcal/day, ≈0.5 kg/week)'
      : goal === 'gain'
        ? 'gain weight (+350 kcal/day)'
        : 'maintain (no adjustment)'
  return {
    title: 'Target — your daily calorie goal',
    body: (
      <>
        <p>What to aim to eat each day: your TDEE adjusted for your goal{custom ? ', or a custom value you set' : ''}.</p>
        <p className="mt-2">
          <strong>Yours:</strong> {t.calorieTarget} kcal/day
          {custom ? ' (custom override).' : goal ? `, from your goal to ${goalText}.` : '.'}
        </p>
        <p className="mt-2">
          <strong>Ideal:</strong> land near this most days. The charts above compare your logged intake against it
          {goal ? `; “${GOAL_LABELS[goal]}” is your current goal (change it in Settings → Profile).` : '.'}
        </p>
      </>
    ),
  }
}

// Display groups (only nutrients with a target appear).
const GROUPS: { title: string; keys: Nutrient[] }[] = [
  { title: 'Energy & macros', keys: ['calories', 'protein', 'carbs', 'fat', 'fibre'] },
  { title: 'Limits (stay under)', keys: ['addedSugar', 'saturatedFat', 'transFat', 'sodium', 'cholesterol', 'alcohol', 'caffeine'] },
  { title: 'Micronutrients', keys: ['omega3', 'potassium', 'calcium', 'iron', 'magnesium', 'zinc', 'vitA', 'vitC', 'vitD', 'b12', 'folate'] },
]

function TargetRow({
  nutrient,
  avg,
  target,
  kind,
}: {
  nutrient: Nutrient
  avg: number
  target: number
  kind: TargetKind
}) {
  const meta = NUTRIENT_LABELS[nutrient]
  const pct = target > 0 ? (avg / target) * 100 : 0
  const isLimit = kind === 'limit'

  let color: string
  if (isLimit) color = pct > 100 ? 'var(--bac-danger)' : 'var(--bac-safe)'
  else if (pct >= 90) color = 'var(--bac-safe)'
  else if (pct >= 60) color = 'var(--accent-orange)'
  else color = 'var(--text-secondary)'

  return (
    <div className="py-1.5">
      <div className="flex justify-between text-sm">
        <span className="text-[var(--text-primary)]">{meta.label}</span>
        <span className="text-[var(--text-secondary)]">
          {Math.round(avg)} / {isLimit ? '≤' : ''}{Math.round(target)} {meta.unit}
        </span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-[var(--bg-card)] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color }} />
      </div>
    </div>
  )
}

export default function NutritionCharts({ data, period }: { data: UserData; period: Period }) {
  const agg = useMemo(() => buildNutrition(data, period), [data, period])
  const targets = useMemo(() => computeTargets(data), [data])
  const windowLabel = periodWindowLabel(period)
  const [info, setInfo] = useState<StatKey | null>(null)
  const infoContent = info && targets ? statInfo(info, targets, data) : null

  if ((data.intake?.length ?? 0) === 0) {
    return (
      <div className="card text-center text-[var(--text-secondary)] mt-4">
        No food &amp; drink logged yet. Use “+ Food” to add your first entry.
      </div>
    )
  }

  const avgKcal = agg.daysLogged ? Math.round(agg.totalCaloriesIn / agg.daysLogged) : 0

  return (
    <div className="space-y-4 mt-4">
      {!targets && (
        <div className="card text-sm text-[var(--accent-orange)]">
          Add your height, date of birth, activity level and goal in <strong>Settings</strong> to unlock
          personalised targets and energy balance.
        </div>
      )}

      {targets && (
        <section className="card">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="font-semibold">Your numbers</h3>
            <span className="text-xs text-[var(--text-secondary)]">tap for detail</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            <Stat label="BMI" value={targets.bmi ? targets.bmi.toFixed(1) : '—'} onClick={() => setInfo('bmi')} />
            <Stat label="BMR" value={`${targets.bmr}`} sub="kcal" onClick={() => setInfo('bmr')} />
            <Stat label="TDEE" value={`${targets.tdee}`} sub="kcal" onClick={() => setInfo('tdee')} />
            <Stat label="Target" value={`${targets.calorieTarget}`} sub="kcal" onClick={() => setInfo('target')} />
          </div>
        </section>
      )}

      <section className="card">
        <h3 className="font-semibold mb-3">Calories consumed</h3>
        <BarChart labels={agg.labels} data={agg.caloriesIn} yLabel="kcal in" />
        <p className="mt-3 text-sm text-[var(--text-secondary)]">
          Logged food on {agg.daysLogged} of {windowLabel} · averaging {avgKcal} kcal per logged day
          {targets ? ` · target ${targets.calorieTarget} kcal` : ''}.
        </p>
      </section>

      {targets && agg.net.length > 0 && (
        <section className="card">
          <h3 className="font-semibold mb-3">Energy balance (consumed − TDEE)</h3>
          <BarChart labels={agg.labels} data={agg.net} yLabel="kcal net" />
          <p className="mt-3 text-sm text-[var(--text-secondary)]">
            Bars above zero are surplus days; below zero, deficit. Balanced against your TDEE of{' '}
            {targets.tdee} kcal (which already accounts for your activity level — logged workouts aren't
            subtracted again).
          </p>
        </section>
      )}

      {targets && (
        <section className="card">
          <h3 className="font-semibold mb-1">Average daily intake vs target</h3>
          <p className="text-xs text-[var(--text-secondary)] mb-3">
            Averaged over your {agg.daysLogged} logged day{agg.daysLogged === 1 ? '' : 's'}.
          </p>
          {GROUPS.map((g) => {
            const rows = g.keys.filter((k) => targets.byNutrient[k] && agg.avgByNutrient[k] != null)
            if (rows.length === 0) return null
            return (
              <div key={g.title} className="mb-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)] mb-1">
                  {g.title}
                </div>
                {rows.map((k) => (
                  <TargetRow
                    key={k}
                    nutrient={k}
                    avg={agg.avgByNutrient[k] || 0}
                    target={targets.byNutrient[k]!.value}
                    kind={targets.byNutrient[k]!.kind}
                  />
                ))}
              </div>
            )
          })}
        </section>
      )}

      <p className="text-xs text-[var(--text-secondary)] text-center">
        Nutrition figures are AI estimates and targets use population reference values (AU/NZ NRVs).
        General information only — not medical advice.
      </p>

      {infoContent && (
        <Modal isOpen onClose={() => setInfo(null)} title={infoContent.title}>
          <div className="text-sm leading-relaxed text-[var(--text-primary)]">{infoContent.body}</div>
        </Modal>
      )}
    </div>
  )
}

function Stat({ label, value, sub, onClick }: { label: string; value: string; sub?: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="rounded-lg p-1 hover:bg-[var(--bg-card)] transition-colors" aria-label={`${label} — details`}>
      <div className="text-lg font-bold text-[var(--text-primary)]">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)]">
        {label}
        {sub ? ` (${sub})` : ''}
      </div>
    </button>
  )
}
